import { calculateDayData, getVisibilityRating, type DayData } from './calendarData';
import { getShootingQualityScore, getDarknessWindowMinutes } from './shootingQualityScore';

export interface DateWindow {
  startDate: Date;
  endDate: Date;
  avgQualityScore: number;
  nightScores: number[];
}

export interface OptimalWindowsResult {
  currentWindow: DateWindow;
  alternatives: DateWindow[];
}

/**
 * Score a single night at a location using the existing MW visibility +
 * shooting quality pipeline.
 */
export function scoreNight(
  location: { latitude: number; longitude: number; name: string },
  date: Date
): { score: number; dayData: DayData } {
  const dayNumber = date.getUTCDate();
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const fullDate = `${monthNames[date.getUTCMonth()]} ${dayNumber}`;

  // Use UTC noon to match how calculateMonthData builds dates
  const utcNoon = new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    12, 0, 0
  ));

  const dayData = calculateDayData(location, utcNoon, dayNumber, fullDate);
  const mwRating = getVisibilityRating(dayData);

  const darknessMinutes = getDarknessWindowMinutes(
    dayData.astroTwilightEvening,
    dayData.astroTwilightMorning
  );

  const score = getShootingQualityScore({
    mwVisibilityRating: mwRating,
    maxElevation: dayData.maxElevation,
    darknessWindowMinutes: darknessMinutes,
  });

  return { score, dayData };
}

/**
 * Find optimal date windows for a trip stop.
 *
 * Given a location and arrival/departure dates, slides a window of the same
 * duration across ±7 days and returns the current window plus up to
 * `maxAlternatives` strictly-better alternatives sorted by average quality.
 */
export function findOptimalDateWindows(
  location: { latitude: number; longitude: number; name: string },
  arrivalDate: Date,
  departureDate: Date,
  options?: { maxAlternatives?: number }
): OptimalWindowsResult {
  const maxAlternatives = options?.maxAlternatives ?? 3;

  // Duration in nights (0 = single-night stay where arrival == departure)
  const msPerDay = 24 * 60 * 60 * 1000;
  const durationDays = Math.round(
    (departureDate.getTime() - arrivalDate.getTime()) / msPerDay
  );

  // Score a window starting at a given date
  const scoreWindow = (start: Date): DateWindow => {
    const nightScores: number[] = [];
    // Score each night from arrival through departure
    for (let i = 0; i <= durationDays; i++) {
      const nightDate = new Date(start.getTime() + i * msPerDay);
      const { score } = scoreNight(location, nightDate);
      nightScores.push(score);
    }
    const avg = nightScores.length > 0
      ? nightScores.reduce((sum, s) => sum + s, 0) / nightScores.length
      : 0;
    return {
      startDate: new Date(start),
      endDate: new Date(start.getTime() + durationDays * msPerDay),
      avgQualityScore: Math.round(avg * 10) / 10,
      nightScores,
    };
  };

  // Build all candidate windows: arrival ± 7 days
  const windows: DateWindow[] = [];
  let currentWindow: DateWindow | null = null;

  for (let offset = -7; offset <= 7; offset++) {
    const candidateStart = new Date(arrivalDate.getTime() + offset * msPerDay);
    const window = scoreWindow(candidateStart);
    if (offset === 0) {
      currentWindow = window;
    } else {
      windows.push(window);
    }
  }

  // Fallback (shouldn't happen — offset 0 always runs)
  if (!currentWindow) {
    currentWindow = scoreWindow(arrivalDate);
  }

  // Keep only alternatives strictly better than current, sorted descending
  const alternatives = windows
    .filter(w => w.avgQualityScore > currentWindow!.avgQualityScore)
    .sort((a, b) => b.avgQualityScore - a.avgQualityScore)
    .slice(0, maxAlternatives);

  return { currentWindow, alternatives };
}
